/**
 * Theme charging sims — REAL physarum (slime mold) agent simulation,
 * the Dark Electric charging animation.
 *
 * The classic GPGPU construction: agents live in a float texture
 * (one texel per agent: x, y in uv space, heading angle, personal
 * random seed). Each frame:
 *
 *   1. UPDATE  — fragment pass over the agent texture. Every agent
 *      senses the trail field at three points (ahead, ±sensor
 *      angle), steers toward the strongest deposit, jitters, and
 *      moves forward. While a finger is on the glass, agents inside
 *      the attraction radius steer toward it — the trail network
 *      visibly re-wires and CONNECTS toward the touch point, the
 *      signature interaction of the reference physarum demo.
 *   2. DEPOSIT — the agents draw themselves as additive GL_POINTS
 *      into the trail field (gl_VertexID → texelFetch, no vertex
 *      attributes at all). Deposit strength pulses with the live
 *      charging power, so the whole network breathes with the
 *      session.
 *   3. DIFFUSE + DECAY — 3x3 blur and exponential fade of the trail.
 *   4. DISPLAY — tone-mapped trail on the pure-black ground:
 *      smoke-grey filaments burning to white-hot cores, matching
 *      Dark Electric's greyscale-smoke identity.
 *
 * Precision note: agent positions want float texels. Under the
 * RGBA8 fallback (no EXT_color_buffer_float — rare) positions
 * quantize and the motion turns blocky but the surface still lives;
 * accepted degradation on the same rationale as the fluid sim's.
 */

import { getOrCreateProgram } from '../glContext.js';
import {
  SIM_VERTEX_SOURCE,
  blitTo,
  bindTexture,
  createSimDoubleTarget,
  disposeSimDoubleTarget,
  resolveSimFormats,
  setupSimQuad,
  type SimDoubleTarget,
} from './simGl.js';

export type PhysarumVariantId = 'physarum-dark';

export interface PhysarumPointerInput {
  x: number;
  y: number;
  dx: number;
  dy: number;
  down: boolean;
  moved: boolean;
}

export interface PhysarumStepInputs {
  pointer: PhysarumPointerInput;
  intensity: number;
  active: boolean;
  timeSec: number;
}

// ── Shaders ──────────────────────────────────────────────────────────

/** Seed agents in a soft disc around the centre with random headings. */
const AGENT_INIT_FRAG = `#version 300 es
precision highp float;
uniform float uSeed;
in vec2 vUv;
out vec4 frag;
float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21) + uSeed);
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
void main() {
  float a = hash(vUv) * 6.2831853;
  float r = 0.12 + 0.30 * sqrt(hash(vUv + 0.17));
  vec2 pos = vec2(0.5) + r * vec2(cos(a * 7.0), sin(a * 7.0));
  float heading = hash(vUv + 0.31) * 6.2831853;
  frag = vec4(pos, heading, hash(vUv + 0.53));
}`;

const AGENT_UPDATE_FRAG = `#version 300 es
precision highp float;
uniform sampler2D uAgents;
uniform sampler2D uTrail;
uniform float uDt;
uniform float uTime;
uniform float uMoveSpeed;
uniform float uTurnSpeed;
uniform float uSensorAngle;
uniform float uSensorDist;
uniform vec2 uPointer;
uniform float uPointerDown;
uniform float uAttract;
in vec2 vUv;
out vec4 frag;

float hash(vec2 p) {
  p = fract(p * vec2(234.34, 435.345));
  p += dot(p, p + 34.23);
  return fract(p.x * p.y);
}
float sense(vec2 pos, float ang) {
  vec2 at = pos + vec2(cos(ang), sin(ang)) * uSensorDist;
  return texture(uTrail, at).r;
}
void main() {
  vec4 state = texelFetch(uAgents, ivec2(gl_FragCoord.xy), 0);
  vec2 pos = state.xy;
  float ang = state.z;
  float seed = state.w;

  // Sense-and-turn (the physarum core).
  float f = sense(pos, ang);
  float fl = sense(pos, ang + uSensorAngle);
  float fr = sense(pos, ang - uSensorAngle);
  float rnd = hash(pos * 91.7 + vec2(uTime * 0.37, seed));
  if (f > fl && f > fr) {
    // Keep heading.
  } else if (fl > fr) {
    ang += uTurnSpeed * uDt;
  } else if (fr > fl) {
    ang -= uTurnSpeed * uDt;
  } else {
    ang += (rnd - 0.5) * 2.0 * uTurnSpeed * uDt;
  }
  // Constant gentle jitter keeps the network alive instead of
  // freezing into fixed highways.
  ang += (hash(pos * 53.1 + uTime) - 0.5) * 0.35 * uDt * 6.0;

  // Touch attraction: agents near the finger steer toward it, so the
  // filaments visibly connect to the touch point. Weight falls off
  // with distance; the shortest-arc blend keeps turns natural.
  if (uPointerDown > 0.5) {
    vec2 to = uPointer - pos;
    float dist = length(to);
    float want = atan(to.y, to.x);
    float diff = mod(want - ang + 3.14159265, 6.2831853) - 3.14159265;
    float w = uAttract * uDt * smoothstep(0.55, 0.05, dist);
    ang += diff * min(1.0, w);
  }

  pos += vec2(cos(ang), sin(ang)) * uMoveSpeed * uDt;

  // Wrap-around world keeps agent density uniform (no edge pile-up).
  pos = fract(pos);

  frag = vec4(pos, ang, seed);
}`;

/** Deposit: agents as additive points. No attributes — gl_VertexID
 *  indexes the agent texture directly. */
const DEPOSIT_VERT = `#version 300 es
precision highp float;
uniform sampler2D uAgents;
uniform int uAgentTexWidth;
void main() {
  ivec2 coord = ivec2(gl_VertexID % uAgentTexWidth, gl_VertexID / uAgentTexWidth);
  vec4 state = texelFetch(uAgents, coord, 0);
  gl_Position = vec4(state.xy * 2.0 - 1.0, 0.0, 1.0);
  gl_PointSize = 1.0;
}`;

const DEPOSIT_FRAG = `#version 300 es
precision highp float;
uniform float uDeposit;
out vec4 frag;
void main() {
  frag = vec4(uDeposit, 0.0, 0.0, 1.0);
}`;

const DIFFUSE_DECAY_FRAG = `#version 300 es
precision highp float;
uniform sampler2D uTrail;
uniform vec2 uTexelSize;
uniform float uDecay;
in vec2 vUv;
out vec4 frag;
void main() {
  float sum = 0.0;
  for (int dx = -1; dx <= 1; dx += 1) {
    for (int dy = -1; dy <= 1; dy += 1) {
      sum += texture(uTrail, vUv + vec2(float(dx), float(dy)) * uTexelSize).r;
    }
  }
  frag = vec4(sum / 9.0 * uDecay, 0.0, 0.0, 1.0);
}`;

/** Pure-black ground, smoke-grey filaments, white-hot cores. The
 *  charge pulse breathes the whole field's brightness. */
const DISPLAY_FRAG = `#version 300 es
precision highp float;
uniform sampler2D uTrail;
uniform float uPulse;
in vec2 vUv;
out vec4 frag;
void main() {
  float t = texture(uTrail, vUv).r * uPulse;
  // Smoke ramp: sub-linear lift for the faint web, hot cores clip
  // toward white with a whisper of silver-blue.
  float body = pow(clamp(t, 0.0, 1.0), 0.65);
  vec3 smoke = vec3(0.62, 0.64, 0.68) * body;
  float hot = smoothstep(0.75, 1.6, t);
  vec3 col = smoke + vec3(0.95, 0.97, 1.0) * hot;
  vec2 c = vUv - 0.5;
  float v = smoothstep(0.80, 0.44, length(c));
  col *= mix(0.35, 1.0, v);
  frag = vec4(col, 1.0);
}`;

// ── Tuning ───────────────────────────────────────────────────────────

/** 128x128 agent texture = 16,384 agents — dense enough for the
 *  reference look, light enough for a phone GPU at the 30fps cap. */
const AGENT_TEX_SIZE = 128;
const MOVE_SPEED = 0.085;        // uv per second
const TURN_SPEED = 7.0;          // rad per second toward sensed trail
const SENSOR_ANGLE = 0.45;       // rad
const SENSOR_DIST = 0.025;       // uv
const ATTRACT_STRENGTH = 9.0;    // touch steering gain
const TRAIL_DECAY_PER_SEC = 0.32; // trail keeps ~32% after 1s
const BASE_DEPOSIT = 0.55;
/** Charge pulse: brightness + deposit breathe at this rate, deeper
 *  with higher live power. */
const PULSE_HZ = 0.45;

interface Pass {
  program: WebGLProgram;
}

export class PhysarumSim {
  private readonly gl: WebGL2RenderingContext;
  private readonly quad: WebGLBuffer;
  private agents: SimDoubleTarget;
  private trail: SimDoubleTarget;
  private updatePass: Pass;
  private depositPass: Pass;
  private diffusePass: Pass;
  private displayPass: Pass;
  private lastPulse = 1;

  private constructor(
    gl: WebGL2RenderingContext,
    quad: WebGLBuffer,
    agents: SimDoubleTarget,
    trail: SimDoubleTarget,
    passes: { updatePass: Pass; depositPass: Pass; diffusePass: Pass; displayPass: Pass },
  ) {
    this.gl = gl;
    this.quad = quad;
    this.agents = agents;
    this.trail = trail;
    this.updatePass = passes.updatePass;
    this.depositPass = passes.depositPass;
    this.diffusePass = passes.diffusePass;
    this.displayPass = passes.displayPass;
  }

  static create(
    gl: WebGL2RenderingContext,
    _variant: PhysarumVariantId,
    canvasSize: number,
    seed: number,
  ): PhysarumSim | null {
    const formats = resolveSimFormats(gl);
    const trailRes = Math.max(128, Math.min(320, Math.round(canvasSize * 0.75)));

    const quad = gl.createBuffer();
    if (!quad) return null;
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );

    const mkQuadPass = (key: string, frag: string): Pass | null => {
      const program = getOrCreateProgram(gl, key, SIM_VERTEX_SOURCE, frag);
      if (!program) return null;
      setupSimQuad(gl, program, quad);
      return { program };
    };
    const initPass = mkQuadPass('sim:physarum:init', AGENT_INIT_FRAG);
    const updatePass = mkQuadPass('sim:physarum:update', AGENT_UPDATE_FRAG);
    const diffusePass = mkQuadPass('sim:physarum:diffuse', DIFFUSE_DECAY_FRAG);
    const displayPass = mkQuadPass('sim:physarum:display', DISPLAY_FRAG);
    // Deposit uses its own attribute-less vertex stage.
    const depositProgram = getOrCreateProgram(gl, 'sim:physarum:deposit', DEPOSIT_VERT, DEPOSIT_FRAG);
    const depositPass = depositProgram ? { program: depositProgram } : null;
    if (!initPass || !updatePass || !diffusePass || !displayPass || !depositPass) {
      gl.deleteBuffer(quad);
      return null;
    }

    // Agent state must not be linearly filtered — texelFetch only.
    const agents = createSimDoubleTarget(gl, AGENT_TEX_SIZE, AGENT_TEX_SIZE, formats, gl.NEAREST);
    const trail = createSimDoubleTarget(gl, trailRes, trailRes, formats);
    if (!agents || !trail) {
      if (agents) disposeSimDoubleTarget(gl, agents);
      if (trail) disposeSimDoubleTarget(gl, trail);
      gl.deleteBuffer(quad);
      return null;
    }

    // Seed the agent field once.
    gl.disable(gl.BLEND);
    gl.useProgram(initPass.program);
    setupSimQuad(gl, initPass.program, quad);
    gl.uniform1f(gl.getUniformLocation(initPass.program, 'uSeed'), (seed % 10000) / 10000);
    blitTo(gl, agents.read);

    return new PhysarumSim(gl, quad, agents, trail, {
      updatePass, depositPass, diffusePass, displayPass,
    });
  }

  dispose(): void {
    const { gl } = this;
    disposeSimDoubleTarget(gl, this.agents);
    disposeSimDoubleTarget(gl, this.trail);
    gl.deleteBuffer(this.quad);
  }

  step(dt: number, inputs: PhysarumStepInputs): void {
    const { gl } = this;
    gl.disable(gl.BLEND);

    // Charge pulse shared by deposit and display: a slow breath whose
    // depth rides the live power (uIntensity 0.2..1).
    const pulse = 1.0
      + Math.sin(inputs.timeSec * Math.PI * 2 * PULSE_HZ) * 0.45 * inputs.intensity;
    this.lastPulse = pulse;

    // 1. Agent update.
    let p = this.updatePass.program;
    gl.useProgram(p);
    setupSimQuad(gl, p, this.quad);
    gl.uniform1i(gl.getUniformLocation(p, 'uAgents'), bindTexture(gl, this.agents.read.texture, 0));
    gl.uniform1i(gl.getUniformLocation(p, 'uTrail'), bindTexture(gl, this.trail.read.texture, 1));
    gl.uniform1f(gl.getUniformLocation(p, 'uDt'), dt);
    gl.uniform1f(gl.getUniformLocation(p, 'uTime'), inputs.timeSec);
    gl.uniform1f(gl.getUniformLocation(p, 'uMoveSpeed'), MOVE_SPEED);
    gl.uniform1f(gl.getUniformLocation(p, 'uTurnSpeed'), TURN_SPEED);
    gl.uniform1f(gl.getUniformLocation(p, 'uSensorAngle'), SENSOR_ANGLE);
    gl.uniform1f(gl.getUniformLocation(p, 'uSensorDist'), SENSOR_DIST);
    gl.uniform2f(gl.getUniformLocation(p, 'uPointer'), inputs.pointer.x, inputs.pointer.y);
    gl.uniform1f(gl.getUniformLocation(p, 'uPointerDown'), inputs.pointer.down ? 1 : 0);
    gl.uniform1f(gl.getUniformLocation(p, 'uAttract'), ATTRACT_STRENGTH);
    blitTo(gl, this.agents.write);
    this.agents.swap();

    // 2. Deposit — additive points into the CURRENT trail (no swap:
    // deposit accumulates onto what diffuse produced last frame).
    p = this.depositPass.program;
    gl.useProgram(p);
    gl.uniform1i(gl.getUniformLocation(p, 'uAgents'), bindTexture(gl, this.agents.read.texture, 0));
    gl.uniform1i(gl.getUniformLocation(p, 'uAgentTexWidth'), AGENT_TEX_SIZE);
    gl.uniform1f(
      gl.getUniformLocation(p, 'uDeposit'),
      BASE_DEPOSIT * dt * 30 * (0.75 + 0.5 * pulse * inputs.intensity),
    );
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.trail.read.fbo);
    gl.viewport(0, 0, this.trail.width, this.trail.height);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);
    gl.drawArrays(gl.POINTS, 0, AGENT_TEX_SIZE * AGENT_TEX_SIZE);
    gl.disable(gl.BLEND);

    // 3. Diffuse + decay into the write buffer, then swap.
    p = this.diffusePass.program;
    gl.useProgram(p);
    setupSimQuad(gl, p, this.quad);
    gl.uniform1i(gl.getUniformLocation(p, 'uTrail'), bindTexture(gl, this.trail.read.texture, 0));
    gl.uniform2f(gl.getUniformLocation(p, 'uTexelSize'), this.trail.texelSize[0], this.trail.texelSize[1]);
    gl.uniform1f(gl.getUniformLocation(p, 'uDecay'), Math.pow(TRAIL_DECAY_PER_SEC, dt));
    blitTo(gl, this.trail.write);
    this.trail.swap();
  }

  render(): void {
    const { gl } = this;
    const p = this.displayPass.program;
    gl.useProgram(p);
    setupSimQuad(gl, p, this.quad);
    gl.uniform1i(gl.getUniformLocation(p, 'uTrail'), bindTexture(gl, this.trail.read.texture, 0));
    gl.uniform1f(gl.getUniformLocation(p, 'uPulse'), this.lastPulse);
    blitTo(gl, null);
  }
}
